import React from "react";
import { DatePicker as AntDatePicker, TimePicker as AntTimePicker } from "../ui";
import type { DatePickerProps, TimePickerProps } from "../ui";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { Dayjs } from "dayjs";
import { normalizeTimeToHHmm } from "../units/format";
import {dateInputFormatOf,dateTimeInputFormatOf} from "../units/format";
import {useUnitsOptional} from "../units/UnitsProvider";
import { KEY_ARROW_DOWN, KEY_ARROW_LEFT, KEY_ARROW_RIGHT, KEY_ARROW_UP } from "../ui/keyboardKeys";
import { END_OF_DAY_ALIASES, END_OF_DAY_TIME, HOURS_PER_DAY } from "./endOfDayTime";

const { RangePicker: AntRangePicker } = AntDatePicker;

dayjs.extend(customParseFormat);

export type ControlledDatePickerProps = DatePickerProps;
export type ControlledTimePickerProps = Omit<TimePickerProps, "parseInput"> & {
  /** false: Uhr-Popup bleibt zu; Tippen und Normalisieren laufen im TimePicker. Default true. */
  popup?: boolean;
};
export type ControlledRangePickerProps = React.ComponentProps<typeof AntRangePicker>;

const dateInputFormats = [
  "DD.MM.YYYY",
  "D.M.YYYY",
  "D.MM.YYYY",
  "DD.M.YYYY",
  "DD.MM.YY",
  "D.M.YY",
  "D.MM.YY",
  "DD.M.YY",
  "DDMMYYYY",
  "DMMYYYY",
  "YYYY-MM-DD",
  "YYYYMMDD",
  "MM/DD/YYYY",
  "M/D/YYYY",
  "MM/DD/YY",
  "M/D/YY",
  "MMDDYYYY",
  "MDDYYYY",
];

// Compact two-digit years stay supported, but only on blur/Enter so typing
// "24121926" is not normalized after the intermediate "241219".
const compactShortYearDateInputFormats = [
  "DDMMYY",
  "DMMYY",
  "MMDDYY",
  "MDDYY",
];

const timeInputFormats = [
  "HH:mm",
  "H:mm",
  "HH:m",
  "H:m",
  "HH.mm",
  "H.mm",
  "HH.m",
  "H.m",
  "HHmm",
  "Hmm",
];

const dateTimeInputFormats = dateInputFormats.flatMap((dateFormat) =>
  timeInputFormats.map((timeFormat) => `${dateFormat} ${timeFormat}`));

const mergeFormats = (
  format: ControlledDatePickerProps["format"] | ControlledTimePickerProps["format"],
  defaults: string[],
) => {
  const formats = Array.isArray(format) ? format : format ? [format] : [];
  const stringFormats = formats.filter((item): item is string => typeof item === "string");
  const functionFormats = formats.filter((item): item is Exclude<typeof item, string> => typeof item !== "string");
  return [...functionFormats, ...Array.from(new Set([...stringFormats, ...defaults]))];
};

const resolveDatePickerFormat = (props: ControlledDatePickerProps) => {
  if (props.picker && props.picker !== "date") return props.format;
  return mergeFormats(props.format, props.showTime ? dateTimeInputFormats : dateInputFormats) as ControlledDatePickerProps["format"];
};

function firstStringFormat(formats: ControlledDatePickerProps["format"]): string {
  if (Array.isArray(formats)) {
    return formats.find((item): item is string => typeof item === "string") ?? "YYYY-MM-DD";
  }
  return typeof formats === "string" ? formats : "YYYY-MM-DD";
}

type DateTimeSegment = {
  unit: "date" | "month" | "year" | "hour" | "minute" | "second";
  start: number;
  end: number;
};

const formatTokenPattern = /(Y+|M+|D+|H+|h+|m+|s+)/g;
/** First character of a format token; H and h fall through to "hour". */
const TOKEN_UNITS: Record<string, DateTimeSegment["unit"]> = { D: "date", M: "month", Y: "year", m: "minute", s: "second" };

function numericSegments(value: Dayjs, format: string): DateTimeSegment[] {
  const segments: DateTimeSegment[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  formatTokenPattern.lastIndex = 0;

  while ((match = formatTokenPattern.exec(format)) !== null) {
    const literal = format.slice(cursor, match.index);
    cursor += literal.length;
    const token = match[0];
    const text = value.format(token);
    const unit = TOKEN_UNITS[token[0]] ?? "hour";
    segments.push({ unit, start: cursor, end: cursor + text.length });
    cursor += text.length;
  }

  return segments;
}

function selectSegment(input: HTMLInputElement, segments: DateTimeSegment[], index: number): void {
  const segment = segments[Math.max(0, Math.min(index, segments.length - 1))];
  if (segment) input.setSelectionRange(segment.start, segment.end);
}

function segmentIndexAtCursor(segments: DateTimeSegment[], cursor: number): number {
  const containingIndex = segments.findIndex((segment) => cursor >= segment.start && cursor <= segment.end);
  if (containingIndex >= 0) return containingIndex;
  const nextIndex = segments.findIndex((segment) => cursor < segment.start);
  return nextIndex >= 0 ? nextIndex : segments.length - 1;
}

function changeDateSegment(value: Dayjs, unit: DateTimeSegment["unit"], direction: 1 | -1): Dayjs {
  switch (unit) {
    case "date": return value.add(direction, "day");
    case "month": {
      const nextMonth = value.date(1).add(direction, "month");
      return nextMonth.date(Math.min(value.date(), nextMonth.daysInMonth()));
    }
    case "year": {
      const nextYear = value.date(1).add(direction, "year");
      return nextYear.date(Math.min(value.date(), nextYear.daysInMonth()));
    }
    default: return value;
  }
}

function changeTimeSegment(value: Dayjs, unit: DateTimeSegment["unit"], direction: 1 | -1, minuteStep?: number): Dayjs {
  if (unit === "minute") return value.add(direction * (minuteStep || 1), "minute");
  if (unit === "hour") return value.add(direction, "hour");
  if (unit === "second") return value.add(direction, "second");
  return value;
}

export const DatePicker = React.forwardRef<any, ControlledDatePickerProps>((props, ref) => {
  const units=useUnitsOptional();
  const {
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseUp,
    picker,
    showTime,
    ...restProps
  } = props;
  const resolvedFormat = resolveDatePickerFormat(units&&props.format==null&&(!props.picker||props.picker==="date")?{...props,format:props.showTime?dateTimeInputFormatOf(units):dateInputFormatOf(units)}:props);
  const shouldCommitDateText = (!picker || picker === "date") && !showTime;
  const displayFormat = firstStringFormat(resolvedFormat);
  // The kit parses only on blur/Enter, so all accepted formats share its draft lifecycle.
  const pickerFormat = shouldCommitDateText
    ? mergeFormats(resolvedFormat, compactShortYearDateInputFormats)
    : resolvedFormat;
  const selectAllOnMouseUp = React.useRef(false);

  const selectDateSegment = React.useCallback((input: HTMLInputElement, index?: number) => {
    if (!value || !shouldCommitDateText) return;
    const segments = numericSegments(value, displayFormat);
    if (!segments.length) return;
    selectSegment(input, segments, index ?? segmentIndexAtCursor(segments, input.selectionStart ?? 0));
  }, [displayFormat, shouldCommitDateText, value]);

  const commitSegmentValue = React.useCallback((next: Dayjs, input: HTMLInputElement, index: number) => {
    if (props.disabledDate?.(next, { type: "date" })) return;
    onChange?.(next, next.format(displayFormat));
    requestAnimationFrame(() => selectDateSegment(input, index));
  }, [displayFormat, onChange, props.disabledDate, selectDateSegment]);

  return (
    <AntDatePicker
      ref={ref}
      {...restProps}
      value={value}
      picker={picker}
      showTime={showTime}
      format={pickerFormat}
      onChange={onChange}
      inputReadOnly={false}
      preserveInvalidOnBlur
      onBlur={(event, info) => {
        onBlur?.(event, info);
      }}
      onFocus={(event, info) => {
        selectAllOnMouseUp.current = true;
        if (event.target instanceof HTMLInputElement) event.target.select();
        onFocus?.(event, info);
      }}
      onMouseUp={(event) => {
        if (selectAllOnMouseUp.current) {
          event.preventDefault();
          selectAllOnMouseUp.current = false;
        } else if (event.target instanceof HTMLInputElement) selectDateSegment(event.target);
        onMouseUp?.(event);
      }}
      onKeyDown={(event, preventDefault) => {
        selectAllOnMouseUp.current = false;
        const input = event.target;
        const segments = input instanceof HTMLInputElement && value && input.value === value.format(displayFormat) && shouldCommitDateText
          ? numericSegments(value, displayFormat)
          : [];
        const currentIndex = input instanceof HTMLInputElement && segments.length
          ? segmentIndexAtCursor(segments, input.selectionStart ?? 0)
          : -1;
        if (input instanceof HTMLInputElement && segments.length && (event.key === KEY_ARROW_LEFT || event.key === KEY_ARROW_RIGHT)) {
          event.preventDefault();
          preventDefault?.();
          selectDateSegment(input, currentIndex + (event.key === KEY_ARROW_LEFT ? -1 : 1));
        } else if (input instanceof HTMLInputElement && segments.length && (event.key === KEY_ARROW_UP || event.key === KEY_ARROW_DOWN)) {
          event.preventDefault();
          preventDefault?.();
          const next = changeDateSegment(value!, segments[currentIndex].unit, event.key === KEY_ARROW_UP ? 1 : -1);
          commitSegmentValue(next, input, currentIndex);
        }
        onKeyDown?.(event, preventDefault);
      }}
    />
  );
});

DatePicker.displayName = "DatePicker";

const TIME_PICKER_DAY_START = "2000-01-01T00:00:00";

function timeValueFromText(text: string, current: Dayjs | null, formats: ControlledTimePickerProps["format"]): Dayjs | null {
  const trimmed = text.trim();
  if (END_OF_DAY_ALIASES.includes(trimmed)) {
    return dayjs(TIME_PICKER_DAY_START).add(HOURS_PER_DAY, "hour");
  }
  const dayStart = dayjs(TIME_PICKER_DAY_START);
  const base = current?.isSame(dayStart.add(1, "day"), "second") ? dayStart : current ?? dayStart;
  // Parse configured formats first, including seconds, before compact HHmm normalization.
  for (const format of (Array.isArray(formats) ? formats : [formats])) {
    if (typeof format !== "string") continue;
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) return base.hour(parsed.hour()).minute(parsed.minute()).second(parsed.second());
  }
  // Reject extra digits and arbitrary text instead of silently extracting a partial time.
  if (!/^\d{1,4}$/.test(trimmed) && !/^\d{1,2}[:.]\d{2}$/.test(trimmed)) return null;
  const normalized = normalizeTimeToHHmm(trimmed);
  if (!normalized) return null;
  const [hours, minutes] = normalized.split(":").map(Number);
  return base.hour(hours).minute(minutes).second(0).millisecond(0);
}

function timePickerDisplayText(value: Dayjs, format: string): string {
  if (!value?.isValid()) return "";
  if (value.isSame(dayjs(TIME_PICKER_DAY_START).add(1, "day"), "second")) return END_OF_DAY_TIME;
  return value.format(format);
}

function isAllowedTime(
  value: Dayjs,
  disabledTime?: ControlledTimePickerProps["disabledTime"],
): boolean {
  const disabled = disabledTime?.(value);
  if (!disabled) return true;
  const hour = value.hour();
  const minute = value.minute();
  const second = value.second();
  if (disabled.disabledHours?.().includes(hour)) return false;
  if (disabled.disabledMinutes?.(hour).includes(minute)) return false;
  if (disabled.disabledSeconds?.(hour, minute).includes(second)) return false;
  return true;
}

export const TimePicker = React.forwardRef<any, ControlledTimePickerProps>((props, ref) => {
  const {
    value,
    onChange,
    onSelect,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseUp,
    format,
    minuteStep,
    disabledTime,
    popup = true,
    placeholder,
    allowClear,
    disabled,
    style,
    className,
    id,
    open,
    onOpenChange,
    ...restProps
  } = props;
  const resolvedFormat = mergeFormats(format, timeInputFormats) as NonNullable<ControlledTimePickerProps["format"]>;
  const displayFormat = firstStringFormat(resolvedFormat);
  const selectAllOnMouseUp = React.useRef(false);

  const parseInput = (text: string, current: Dayjs | null) => {
    const next = timeValueFromText(text, current, resolvedFormat);
    return next && isAllowedTime(next, disabledTime) ? next : null;
  };
  const pickerFormat = [
    (date: Dayjs) => {
      const firstFormat = Array.isArray(resolvedFormat) ? resolvedFormat[0] : resolvedFormat;
      return typeof firstFormat === "function" ? firstFormat(date) : timePickerDisplayText(date, displayFormat);
    },
    ...(Array.isArray(resolvedFormat) ? resolvedFormat : [resolvedFormat]),
  ];

  const selectTimeSegment = React.useCallback((input: HTMLInputElement, index?: number) => {
    if (!value) return;
    const segments = numericSegments(value, displayFormat);
    if (!segments.length) return;
    selectSegment(input, segments, index ?? segmentIndexAtCursor(segments, input.selectionStart ?? 0));
  }, [displayFormat, value]);

  const commitSegmentValue = React.useCallback((next: Dayjs, input: HTMLInputElement, index: number) => {
    if (!isAllowedTime(next, disabledTime)) return;
    onChange?.(next, next.format(displayFormat));
    onSelect?.(next);
    requestAnimationFrame(() => selectTimeSegment(input, index));
  }, [disabledTime, displayFormat, onChange, onSelect, selectTimeSegment]);

  return (
    <AntTimePicker
      ref={ref}
      {...restProps}
      value={value}
      onChange={onChange}
      onSelect={onSelect}
      format={pickerFormat}
      parseInput={parseInput}
      minuteStep={minuteStep}
      disabledTime={disabledTime}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      style={style}
      className={className}
      id={id}
      open={popup ? open : false}
      onOpenChange={popup ? onOpenChange : () => undefined}
      inputReadOnly={false}
      preserveInvalidOnBlur
      onBlur={(event) => {
        (onBlur as React.FocusEventHandler<HTMLInputElement> | undefined)?.(event as unknown as React.FocusEvent<HTMLInputElement>);
      }}
      onFocus={(event) => {
        selectAllOnMouseUp.current = true;
        (event.target as HTMLInputElement).select();
        (onFocus as React.FocusEventHandler<HTMLInputElement> | undefined)?.(event as unknown as React.FocusEvent<HTMLInputElement>);
      }}
      onMouseUp={(event) => {
        if (selectAllOnMouseUp.current) {
          event.preventDefault();
          selectAllOnMouseUp.current = false;
        } else if (event.target instanceof HTMLInputElement) selectTimeSegment(event.target);
        onMouseUp?.(event);
      }}
      onKeyDown={(event, preventDefault) => {
        selectAllOnMouseUp.current = false;
        const input = event.currentTarget as HTMLInputElement;
        const segments = value && input.value === timePickerDisplayText(value, displayFormat) ? numericSegments(value, displayFormat) : [];
        const currentIndex = segments.length ? segmentIndexAtCursor(segments, input.selectionStart ?? 0) : -1;
        if (segments.length && (event.key === KEY_ARROW_LEFT || event.key === KEY_ARROW_RIGHT)) {
          event.preventDefault();
          preventDefault?.();
          selectTimeSegment(input, currentIndex + (event.key === KEY_ARROW_LEFT ? -1 : 1));
        } else if (segments.length && (event.key === KEY_ARROW_UP || event.key === KEY_ARROW_DOWN)) {
          event.preventDefault();
          preventDefault?.();
          const next = changeTimeSegment(value!, segments[currentIndex].unit, event.key === KEY_ARROW_UP ? 1 : -1, minuteStep);
          commitSegmentValue(next, input, currentIndex);
        }
        onKeyDown?.(event, preventDefault);
      }}
    />
  );
});

TimePicker.displayName = "TimePicker";

export const RangePicker = React.forwardRef<any, ControlledRangePickerProps>((props, ref) => {
  const units=useUnitsOptional();
  return <AntRangePicker ref={ref} {...props} format={props.format??(units&&(!props.picker||props.picker==="date")?(props.showTime?dateTimeInputFormatOf(units):dateInputFormatOf(units)):undefined)} />;
});

RangePicker.displayName = "RangePicker";
